import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MessageSquare, Heart, Bookmark, Share2, Flag, Send, Pin, Loader2, X, Trash2,
} from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { SectionLoading, Avatar } from '@/components/community/ui'
import postService, { type Post } from '@/services/postService'
import {
  pinnedAnnouncements, createReport,
  type AnnouncementResponse,
} from '@/services/communityApi'
import { useAuthStore } from '@/stores/authStore'
import { formatDate } from '@/lib/utils'

const REPORT_REASONS = ['SPAM', 'HARASSMENT', 'MISINFORMATION', 'OFF_TOPIC', 'OTHER'] as const

export default function CommunityFeedPage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const { user } = useAuthStore()
  const [posts, setPosts] = useState<Post[]>([])
  const [pinned, setPinned] = useState<AnnouncementResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [reportFor, setReportFor] = useState<Post | null>(null)
  const [composer, setComposer] = useState('')
  const [posting, setPosting] = useState(false)

  const load = useCallback(async () => {
    if (!community) return
    setLoading(true)
    const [feed, pin] = await Promise.allSettled([
      postService.getFeedPosts(0, 15),
      pinnedAnnouncements(community.id),
    ])
    if (feed.status === 'fulfilled') {
      const r: any = feed.value
      setPosts(r?.data?.content || r?.data || [])
    }
    if (pin.status === 'fulfilled') setPinned(pin.value)
    setLoading(false)
  }, [community])

  useEffect(() => { load() }, [load])

  const submitPost = async () => {
    const content = composer.trim()
    if (!content) return
    setPosting(true)
    try {
      const res: any = await postService.createPost({ content, postType: 'TEXT', visibility: 'PUBLIC' })
      const created: Post = res?.data
      if (created) setPosts((p) => [created, ...p])
      setComposer('')
    } catch { /* ignore */ } finally { setPosting(false) }
  }

  const onLike = async (p: Post) => {
    try {
      const res: any = await postService.toggleLike(p.id)
      const upd: Post = res?.data
      setPosts((prev) => prev.map((x) => x.id === p.id ? (upd ?? { ...x, isLiked: !x.isLiked, likeCount: x.likeCount + (x.isLiked ? -1 : 1) }) : x))
    } catch { /* ignore */ }
  }
  const onSave = async (p: Post) => {
    try {
      await postService.toggleSave(p.id)
      setPosts((prev) => prev.map((x) => x.id === p.id ? { ...x, isSaved: !x.isSaved } : x))
    } catch { /* ignore */ }
  }
  const onShare = async (p: Post) => {
    try {
      const res: any = await postService.sharePost(p.id)
      const upd: Post = res?.data
      setPosts((prev) => prev.map((x) => x.id === p.id ? (upd ?? { ...x, shareCount: x.shareCount + 1 }) : x))
    } catch { /* ignore */ }
  }
  const onDelete = async (p: Post) => {
    if (!window.confirm(t('community.feed.deleteConfirm', 'Delete this post permanently? This cannot be undone.'))) return
    try {
      await postService.deletePost(p.id)
      setPosts((prev) => prev.filter((x) => x.id !== p.id))
    } catch {
      alert(t('community.feed.deleteError', 'Could not delete the post. Please try again.'))
    }
  }

  if (!community) return null

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.feed.title')} subtitle={t('community.feed.subtitle')} icon={MessageSquare} />

      {/* Pinned announcements banner */}
      {pinned.length > 0 && (
        <div className="mb-4 space-y-3">
          {pinned.map((a) => (
            <div key={a.id} className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
              <Pin className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">{a.title}</p>
                <p className="text-sm text-amber-800 line-clamp-2">{a.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <GovCard className="mb-4">
        <div className="flex items-start gap-3">
          <Avatar name={community.leader.fullName} size={40} />
          <div className="flex-1">
            <textarea value={composer} onChange={(e) => setComposer(e.target.value)} rows={2}
              placeholder={t('community.feed.composerPlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none resize-none" />
            <div className="flex justify-end mt-2">
              <GovButton variant="primary" size="sm" onClick={submitPost} disabled={posting || !composer.trim()}>
                {posting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4 mr-1" aria-hidden="true" />}
                {t('community.feed.post')}
              </GovButton>
            </div>
          </div>
        </div>
      </GovCard>

      {/* Feed */}
      {loading ? <SectionLoading rows={4} /> : posts.length === 0 ? (
        <GovEmptyState icon="💬" title={t('community.feed.empty')} description={t('community.feed.emptyHint')} />
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <FeedPostCard key={p.id} post={p} onLike={onLike} onSave={onSave} onShare={onShare} onReport={() => setReportFor(p)} canDelete={!!user && p.user.id === user.id} onDelete={onDelete} />
          ))}
        </div>
      )}

      {reportFor && <ReportModal post={reportFor} communityId={community.id} onClose={() => setReportFor(null)} />}
    </CommunityLayout>
  )
}

function FeedPostCard({ post, onLike, onSave, onShare, onReport, canDelete, onDelete }: {
  post: Post
  onLike: (p: Post) => void
  onSave: (p: Post) => void
  onShare: (p: Post) => void
  onReport: () => void
  canDelete?: boolean
  onDelete?: (p: Post) => Promise<void>
}) {
  const { t } = useTranslation()
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState<any[]>([])
  const [commentText, setCommentText] = useState('')
  const [loadingComments, setLoadingComments] = useState(false)
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    if (!onDelete || deleting) return
    setDeleting(true)
    try { await onDelete(post) } finally { setDeleting(false) }
  }

  const openComments = async () => {
    if (showComments) { setShowComments(false); return }
    setShowComments(true)
    if (post.comments?.length) { setComments(post.comments); return }
    setLoadingComments(true)
    try {
      const res: any = await postService.getComments(post.id, 0, 20)
      setComments(res?.data?.content || res?.data || [])
    } catch { setComments([]) } finally { setLoadingComments(false) }
  }

  const addComment = async () => {
    const text = commentText.trim()
    if (!text) return
    setBusy(true)
    try {
      const res: any = await postService.addComment(post.id, text)
      const c = res?.data
      if (c) setComments((p) => [...p, c])
      setCommentText('')
    } catch { /* ignore */ } finally { setBusy(false) }
  }

  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <Avatar name={`${post.user.firstName} ${post.user.lastName}`} image={post.user.profileImage} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{post.user.firstName} {post.user.lastName}</span>
            <span className="text-xs text-gray-400">{formatDate(post.createdAt)}</span>
          </div>
          <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{post.content}</p>
          {post.media && post.media.length > 0 && (
            <div className="mt-2 grid gap-1">
              {post.media.map((m) => (
                m.mediaType === 'IMAGE'
                  ? <img key={m.id} src={m.mediaUrl} alt="" className="rounded-lg max-h-80 object-cover w-full" />
                  : <video key={m.id} src={m.mediaUrl} controls className="rounded-lg max-h-80 w-full" />
              ))}
            </div>
          )}

          {/* Action row */}
          <div className="flex items-center gap-1 mt-3 flex-wrap text-sm">
            <ActionBtn onClick={() => onLike(post)} active={post.isLiked} icon={Heart} label={String(post.likeCount)} title={t('community.feed.like')} />
            <ActionBtn onClick={openComments} icon={MessageSquare} label={String(post.commentCount)} title={t('community.feed.comment')} />
            <ActionBtn onClick={() => onShare(post)} icon={Share2} label={String(post.shareCount)} title={t('community.feed.share')} />
            <ActionBtn onClick={() => onSave(post)} active={post.isSaved} icon={Bookmark} label="" title={t('community.feed.save')} />
            <ActionBtn onClick={onReport} icon={Flag} label="" title={t('community.feed.report')} />
            {canDelete && onDelete && (
              <ActionBtn onClick={handleDelete} icon={Trash2} label="" title={t('community.feed.delete', 'Delete')} danger disabled={deleting} />
            )}
          </div>

          {/* Comments */}
          {showComments && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
              <div className="flex gap-2">
                <input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addComment() }}
                  placeholder={t('community.feed.writeComment')}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none min-h-[40px]" />
                <button onClick={addComment} disabled={busy || !commentText.trim()}
                  className="px-3 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-50 min-h-[40px]">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              {loadingComments ? <p className="text-xs text-gray-400">{t('common.saving')}</p> : comments.map((c: any) => (
                <div key={c.id} className="flex items-start gap-2">
                  <Avatar name={`${c.user?.firstName ?? ''} ${c.user?.lastName ?? ''}`} image={c.user?.profileImage} size={28} />
                  <div className="min-w-0">
                    <p className="text-sm"><span className="font-medium text-gray-900">{c.user?.firstName} {c.user?.lastName}</span> <span className="text-gray-700">{c.content}</span></p>
                    <p className="text-xs text-gray-400">{formatDate(c.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GovCard>
  )
}

function ActionBtn({ onClick, icon: Icon, label, title, active, danger, disabled }: {
  onClick: () => void
  icon: React.ComponentType<{ className?: string }>
  label: string
  title: string
  active?: boolean
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button onClick={onClick} title={title} aria-label={title} disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 min-h-[40px] disabled:opacity-50 ${danger ? 'text-red-600 hover:bg-red-50' : active ? 'text-red-600' : 'text-gray-500'}`}>
      <Icon className="w-4 h-4" aria-hidden="true" />{label && <span className="text-xs">{label}</span>}
    </button>
  )
}

function ReportModal({ post, communityId, onClose }: { post: Post; communityId: number; onClose: () => void }) {
  const { t } = useTranslation()
  const [reason, setReason] = useState<string>('MISINFORMATION')
  const [details, setDetails] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError(null)
    try {
      await createReport({ postId: post.id, communityId, reason, details: details || undefined })
      setDone(true)
    } catch (err: any) { setError(err?.response?.data?.message || t('errors.generic')) }
    finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('community.feed.report')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <GovCard className="relative w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('community.feed.report')}</h2>
          <button onClick={onClose} aria-label={t('community.actions.cancel')} className="p-2 rounded-lg hover:bg-gray-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <p className="text-sm text-emerald-600 py-4 text-center">{t('community.feed.reportSent')}</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.feed.reportReason')}</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none min-h-[44px]">
                {REPORT_REASONS.map((r) => <option key={r} value={r}>{t(`community.settings.reason.${r}`, r)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.feed.reportDetails')}</label>
              <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none" />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <GovButton variant="secondary" size="sm" onClick={onClose}>{t('community.actions.cancel')}</GovButton>
              <GovButton variant="primary" size="sm" type="submit" disabled={saving}>{saving ? t('common.saving') : t('community.feed.submitReport')}</GovButton>
            </div>
          </form>
        )}
      </GovCard>
    </div>
  )
}